import type { DateSelectArg, EventInput } from "@fullcalendar/core";
import { atom, getDefaultStore, useAtomValue, useStore } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { parseISOToDate } from "@/lib/date-utils.ts";
import {
  decomposeBoxSelection,
  findEventsInRanges,
  type MatchedEvent,
  type TimeRange,
  toggleTimeRanges,
} from "./time-range-selection.ts";

// FullCalendar requires inline color values for background events; actual styling
// is handled by the .time-range-selected CSS class which overrides these with !important.
// Both use brand-600 (same color) intentionally — the CSS class controls the visual difference.
const SELECTED_COLOR = "var(--color-brand-600)";

// All time-range-select calendars configure a 30-min slot duration (see CalendarView's
// CAROUSEL_VIEW_CONFIG and callers passing slotDuration="00:30:00").
const TIMEGRID_SLOT_MINUTES = 30;

// Window used to dedupe a pair of FC callbacks fired by a single gesture.
// A tap-hold-release (no drag) on a cell can cause FC to emit BOTH `dateClick`
// and `select` for the same release, in either order. Whichever fires second
// is skipped if the first fired within this window.
// Tight enough (100ms) to not eat genuine rapid taps — a human can't produce
// two separate taps within 100ms (pointerdown/up cycle alone is ~30-50ms,
// and a second tap needs another full cycle). FC's same-gesture callbacks
// fire within a few ms of each other, so 100ms is ample.
const GESTURE_DEDUPE_MS = 100;

// ─── Shared selection store ─────────────────────────────────────────────────
// The selection lives in a module atom rather than component state so a calendar
// focus link (`/calendar?r=…`) can highlight ranges without reaching into
// whichever surface happens to be mounted. Colocated with its sole consumer, the
// hook below; the atom itself is module-private and reached through the exports.

/**
 * Wall-clock window a set of events was fetched for. A range's matches can only
 * be trusted once the loaded window covers it — outside that window "no matching
 * events" and "we haven't looked there yet" are indistinguishable.
 */
export type LoadedEventRange = { start: Date; end: Date };

/**
 * Whether the selection holds anything the user asked to act on. `"drag"` means
 * it still contains time the user put there themselves — drawn, tapped or
 * resized on the grid — an act of intent, which is what the range-selection
 * agent popover answers. `"link"` means everything highlighted came from a
 * calendar focus link (an artifact date link, an agent day-range link): the user
 * asked to *look* at a time, not to do something with it, so the highlight and
 * its omnibox chip stand on their own and no popover opens. An empty selection
 * reads as `"link"` — there is nothing to prompt about either way.
 */
export type SelectionOrigin = "drag" | "link";

/** A span of time in absolute ms — see {@link CalendarSelectionState.drawnWindows}. */
type TimeWindow = { startMs: number; endMs: number };

type CalendarSelectionState = {
  ranges: TimeRange[];
  matchedEvents: MatchedEvent[];
  /**
   * Ids of ranges whose matches have been computed against a loaded event
   * window. Matches are monotonic within a range's lifetime — once captured they
   * are never recomputed, so paging to a window that no longer covers the range
   * can't silently empty the agent's context. They're dropped only when the
   * range itself is.
   */
  resolvedRangeIds: string[];
  /**
   * The spans a grid gesture put into the selection. What the popover turns on
   * is whether any of this time is still highlighted.
   *
   * Recording what the user *added*, rather than what a link seeded, is what
   * keeps the answer stable: the two describe the same split, but only this one
   * survives the selection being reshaped. Ranges carry ids derived from their
   * own start and end, so drawing beside a highlight fuses the pair under a
   * combined id (see `mergeRanges`) and splitting it mints another — any mark on
   * a range or its id is gone after one merge. Time is not renamed by either.
   *
   * Windows are dropped once nothing overlaps them, so an hour the user selected
   * and then cleared doesn't keep the prompt armed, and a link that re-seeds the
   * selection wipes them outright.
   */
  drawnWindows: TimeWindow[];
};

const EMPTY_SELECTION: CalendarSelectionState = {
  ranges: [],
  matchedEvents: [],
  resolvedRangeIds: [],
  drawnWindows: [],
};

const windowOf = (r: TimeRange): TimeWindow => ({
  startMs: Date.parse(r.startISO),
  endMs: Date.parse(r.endISO),
});

const overlaps = (r: TimeRange, w: TimeWindow): boolean =>
  Date.parse(r.startISO) < w.endMs && Date.parse(r.endISO) > w.startMs;

/** See {@link SelectionOrigin}. */
function selectionOrigin(ranges: TimeRange[], drawn: TimeWindow[]): SelectionOrigin {
  return ranges.some((r) => drawn.some((w) => overlaps(r, w))) ? "drag" : "link";
}

/** Forget windows nothing highlights any more — see `drawnWindows`. */
function pruneWindows(ranges: TimeRange[], drawn: TimeWindow[]): TimeWindow[] {
  return drawn.filter((w) => ranges.some((r) => overlaps(r, w)));
}

/**
 * Deliberately **not** persisted, unlike the shared view/date atoms: a highlighted
 * range keeps an omnibox chip registered and suppresses the implicit
 * visible-range context, so it changes what the agent is told. Surviving a reload
 * would make that invisible to the user; surviving in-tab navigation is enough.
 */
const calendarSelectionAtom = atom<CalendarSelectionState>(EMPTY_SELECTION);

/** Add matches without duplicating an `(urn, rangeId)` pair already captured. */
function mergeMatches(prev: MatchedEvent[], added: MatchedEvent[]): MatchedEvent[] {
  if (added.length === 0) return prev;
  const existing = new Set(prev.map((e) => `${e.urn}::${e.rangeId}`));
  const unique = added.filter((e) => !existing.has(`${e.urn}::${e.rangeId}`));
  return unique.length > 0 ? [...prev, ...unique] : prev;
}

/** Whether a loaded event window fully covers a range, so its matches are final. */
function isRangeLoaded(range: TimeRange, loaded: LoadedEventRange): boolean {
  return (
    Date.parse(range.startISO) >= loaded.start.getTime() &&
    Date.parse(range.endISO) <= loaded.end.getTime()
  );
}

/**
 * Replace the highlighted ranges from outside a calendar surface — the calendar
 * focus boundary's write when a `/calendar?r=…` link is claimed.
 *
 * The caller has no events to match against (it may be focusing a day that was
 * never fetched), so the new ranges start unresolved and the mounted surface
 * fills their matches in once the covering window loads.
 */
export function replaceCalendarRanges(ranges: TimeRange[]): void {
  getDefaultStore().set(calendarSelectionAtom, {
    ranges,
    matchedEvents: [],
    resolvedRangeIds: [],
    // A link replaces the selection outright, so nothing in it is the user's.
    drawnWindows: [],
  });
}

/** Test-only: drop the shared selection between cases. */
export function __resetCalendarSelection(): void {
  getDefaultStore().set(calendarSelectionAtom, EMPTY_SELECTION);
}

/**
 * Pure computation: apply a selection toggle and compute the state diff.
 * @param prev - Current time ranges before this selection
 * @param dayRanges - Per-day ranges from decomposeBoxSelection
 * @param allDay - Whether this is a full-day (month view) selection
 * @param calendarEvents - Calendar events to match against new ranges
 * @returns next (updated ranges), newMatches (events overlapping added ranges),
 *          removedIds (IDs of ranges that were subtracted)
 */
function computeSelectionUpdate(
  prev: TimeRange[],
  dayRanges: { start: Date; end: Date }[],
  allDay: boolean,
  calendarEvents: EventInput[],
): {
  next: TimeRange[];
  newMatches: MatchedEvent[];
  removedIds: Set<string>;
  addedWindows: TimeWindow[];
} {
  let next = prev;
  // Which of these gestures put time in rather than taking it out. Decided here,
  // inside the fold, because it depends on what is selected at that point —
  // and taken from the gesture itself rather than from the resulting ranges,
  // which may have absorbed a neighbour that the user did not draw.
  const addedWindows: TimeWindow[] = [];
  for (const { start, end } of dayRanges) {
    const startMs = start.getTime();
    const endMs = end.getTime();
    // The same overlap test `toggleTimeRanges` makes: overlap subtracts, a clear
    // span adds.
    const subtracts = next.some(
      (r) => startMs < Date.parse(r.endISO) && endMs > Date.parse(r.startISO),
    );
    if (!subtracts) addedWindows.push({ startMs, endMs });
    next = toggleTimeRanges(next, start, end, allDay);
  }
  const addedRanges = next.filter((r) => !prev.some((p) => p.id === r.id));
  const removedIds = new Set(prev.filter((p) => !next.some((r) => r.id === p.id)).map((p) => p.id));
  const newMatches = addedRanges.length > 0 ? findEventsInRanges(calendarEvents, addedRanges) : [];
  return { next, newMatches, removedIds, addedWindows };
}

/**
 * Reusable hook for calendar time range selection, backed by the shared
 * selection store above so every surface reads one selection and a focus link
 * can write it without a handle on the mounted surface.
 *
 * All selection mutations flow through a single path — `toggleTimeRanges` via
 * `applyRangeToggle`. Two FullCalendar callbacks feed it:
 *   - `handleSelect` — drag selections (box across cells/days)
 *   - `handleDateClick` — single-cell taps on empty slots
 *
 * Taps on already-selected cells also route through `handleDateClick` because
 * `.fc-bg-event.time-range-selected` has `pointer-events: none` in CSS, so the
 * tap passes through to the underlying slot. `toggleTimeRanges` handles the
 * subtract case (overlap ⇒ punch hole) symmetrically with the add case.
 *
 * Pass `calendarEvents` so matches for a user-drawn range are captured at
 * selection time (before the user navigates away and the events leave the query
 * cache). `loadedRange` is the window those events cover; it's what lets the
 * reconcile effect below tell "this day has no events" apart from "this day was
 * never fetched" for ranges that arrived from a link.
 */
export function useCalendarSelection(
  calendarEvents: EventInput[],
  loadedRange?: LoadedEventRange | null,
) {
  const store = useStore();
  const { ranges, matchedEvents, resolvedRangeIds, drawnWindows } =
    useAtomValue(calendarSelectionAtom);
  const origin = useMemo(() => selectionOrigin(ranges, drawnWindows), [ranges, drawnWindows]);
  // Ref so callbacks read fresh events without re-creating themselves.
  const eventsRef = useRef(calendarEvents);
  eventsRef.current = calendarEvents;
  // Timestamp of the last toggle (either handleSelect or handleDateClick).
  // Used bidirectionally: if one fires, the other will skip within GESTURE_DEDUPE_MS.
  // A long-press-no-drag release can cause FC to emit both callbacks for the same
  // gesture, in either order, and without this guard the second one reverts the first
  // (cell "flashes" to the new state then back to the original).
  const lastToggleAtRef = useRef(0);

  // Fill in matches for ranges that arrived from a link (unresolved) once the
  // loaded window actually covers them. Monotonic: already-captured matches are
  // never recomputed, so paging away from a range can't empty its matches.
  useEffect(() => {
    if (!loadedRange) return;
    const resolved = new Set(resolvedRangeIds);
    const pending = ranges.filter((r) => !resolved.has(r.id) && isRangeLoaded(r, loadedRange));
    if (pending.length === 0) return;
    const found = findEventsInRanges(calendarEvents, pending);
    // Functional update: the guard above reads rendered state, so the write
    // re-derives from whatever the store holds by the time it lands.
    store.set(calendarSelectionAtom, (prev) => ({
      ...prev,
      matchedEvents: mergeMatches(prev.matchedEvents, found),
      resolvedRangeIds: [...prev.resolvedRangeIds, ...pending.map((r) => r.id)],
    }));
  }, [calendarEvents, loadedRange, ranges, resolvedRangeIds, store]);

  // Apply a toggle to the current ranges. MUST settle synchronously when called
  // from handleSelect — state must land before FullCalendar's deferred
  // unselect() removes the drag mirror. Reading through the store (rather than a
  // ref mirroring rendered state) means rapid follow-up calls in the same tick
  // already see the previous one's write.
  const applyRangeToggle = useCallback(
    (dayRanges: { start: Date; end: Date }[], allDay: boolean) => {
      const prev = store.get(calendarSelectionAtom);
      const { next, newMatches, removedIds, addedWindows } = computeSelectionUpdate(
        prev.ranges,
        dayRanges,
        allDay,
        eventsRef.current,
      );
      const addedIds = next.filter((r) => !prev.ranges.some((p) => p.id === r.id)).map((r) => r.id);
      store.set(calendarSelectionAtom, {
        ranges: next,
        matchedEvents: mergeMatches(
          removedIds.size > 0
            ? prev.matchedEvents.filter((e) => !removedIds.has(e.rangeId))
            : prev.matchedEvents,
          newMatches,
        ),
        // A user-drawn range is matched against the events already on screen, so
        // it's resolved the moment it's created.
        resolvedRangeIds: [
          ...prev.resolvedRangeIds.filter((id) => !removedIds.has(id)),
          ...addedIds,
        ],
        // The seeded windows stand; what changed is how much of the selection
        // now falls outside them, which is what decides the origin. Windows the
        // toggle left nothing highlighting are dropped.
        drawnWindows: pruneWindows(next, [...prev.drawnWindows, ...addedWindows]),
      });
    },
    [store],
  );

  const handleSelect = useCallback(
    (info: DateSelectArg) => {
      // Dedupe: if dateClick just ran for the same gesture, skip.
      if (Date.now() - lastToggleAtRef.current < GESTURE_DEDUPE_MS) return;
      lastToggleAtRef.current = Date.now();
      applyRangeToggle(decomposeBoxSelection(info.start, info.end, info.allDay), info.allDay);
    },
    [applyRangeToggle],
  );

  // Tap on an empty cell: add a single 30-min slot (timegrid) or single day (month).
  // Tap on an already-selected cell: same path — `toggleTimeRanges` detects the
  // overlap and subtracts just that cell. Works because bg-events have
  // `pointer-events: none`, letting the tap hit the underlying slot.
  const handleDateClick = useCallback(
    (info: { date: Date; allDay: boolean }) => {
      // Dedupe: if select just ran for the same gesture, skip.
      if (Date.now() - lastToggleAtRef.current < GESTURE_DEDUPE_MS) return;
      lastToggleAtRef.current = Date.now();
      const end = new Date(info.date);
      if (info.allDay) {
        end.setDate(end.getDate() + 1);
      } else {
        end.setMinutes(end.getMinutes() + TIMEGRID_SLOT_MINUTES);
      }
      applyRangeToggle([{ start: info.date, end }], info.allDay);
    },
    [applyRangeToggle],
  );

  const removeRange = useCallback(
    (rangeId: string) => {
      const prev = store.get(calendarSelectionAtom);
      const ranges = prev.ranges.filter((r) => r.id !== rangeId);
      store.set(calendarSelectionAtom, {
        ranges,
        matchedEvents: prev.matchedEvents.filter((e) => e.rangeId !== rangeId),
        resolvedRangeIds: prev.resolvedRangeIds.filter((id) => id !== rangeId),
        // Dropping a chip narrows the selection; what's left keeps the
        // provenance it had, so dismissing a drawn range beside a link-seeded
        // highlight leaves that highlight reading as the link's.
        drawnWindows: pruneWindows(ranges, prev.drawnWindows),
      });
    },
    [store],
  );

  const clearAll = useCallback(() => {
    store.set(calendarSelectionAtom, EMPTY_SELECTION);
  }, [store]);

  /**
   * Resize a single range to a new start/end window. Used by the mobile
   * iOS-style drag handles. Removes the old range, inserts a new one with
   * an id derived from the new times, and re-runs event matching for the
   * replacement range.
   */
  const resizeRange = useCallback(
    (id: string, newStart: Date, newEnd: Date) => {
      const startISO = newStart.toISOString();
      const endISO = newEnd.toISOString();
      const prev = store.get(calendarSelectionAtom);
      const original = prev.ranges.find((r) => r.id === id);
      if (!original) return;
      const replacement: TimeRange = {
        id: `${startISO}__${endISO}`,
        startISO,
        endISO,
        allDay: original.allDay,
      };
      const ranges = prev.ranges.map((r) => (r.id === id ? replacement : r));
      store.set(calendarSelectionAtom, {
        ranges,
        matchedEvents: [
          ...prev.matchedEvents.filter((e) => e.rangeId !== id),
          ...findEventsInRanges(eventsRef.current, [replacement]),
        ],
        // The resized range was drawn against the events on screen, so it
        // replaces the original's resolved entry rather than going back to
        // pending.
        resolvedRangeIds: [
          ...prev.resolvedRangeIds.filter((rangeId) => rangeId !== id),
          replacement.id,
        ],
        // Dragging a handle is the same act of intent as drawing the range, so
        // wherever it ends up is the user's time — including a handle dragged
        // inwards, which lands inside what a link had seeded.
        drawnWindows: pruneWindows(ranges, [...prev.drawnWindows, windowOf(replacement)]),
      });
    },
    [store],
  );

  const additionalEvents: EventInput[] = useMemo(
    () =>
      ranges.map((r) => ({
        id: r.id,
        start: parseISOToDate(r.startISO),
        end: parseISOToDate(r.endISO),
        // FullCalendar's dayGrid (month view) only renders background events that have
        // allDay: true — its internal splitAllDaySegsByRow filters out timed bg-events.
        allDay: r.allDay,
        display: "background" as const,
        backgroundColor: SELECTED_COLOR,
        borderColor: SELECTED_COLOR,
        classNames: ["time-range-selected"],
        extendedProps: { isTimeRangeSelection: true },
      })),
    [ranges],
  );

  return {
    ranges,
    matchedEvents,
    additionalEvents,
    origin,
    handleSelect,
    handleDateClick,
    removeRange,
    clearAll,
    resizeRange,
  };
}
